import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import CanvasMap from '../components/common/CanvasMap';
import { MapRegion, REGIONS, CityData, ElectionResult } from '../types/MapTypes';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import citiesWithCoordinates from '../data/cities_with_coordinates.json';
import ElectionHistoryCell from '../components/common/ElectionHistoryCell';
import { CityDetailModal } from '../components/common/CityDetailModal';
import { CityModal, CityModalRegistro, CityModalDemanda, CityModalContato } from '../components/common/CityModal';
import { DemandDetailModal } from '../components/common/DemandDetailModal';
import NewDemandModal, { DemandFormData } from '../components/common/NewDemandModal';
import UserAvatar from '../components/common/UserAvatar';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

const PageContainer = styled.div`
  height: calc(100vh - 56px);
  width: 100vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 700;
  color: var(--accent);
  white-space: nowrap;

  span {
    font-size: 14px;
    font-weight: 400;
    color: #64748b;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  justify-content: flex-end;
`;

const ToggleContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 4px;
  display: flex;
  gap: 2px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => props.$active ? `
    background: rgba(var(--accent-rgb), 0.08);
    color: var(--accent);
    box-shadow: 0 1px 3px rgba(var(--accent-rgb), 0.15);
  ` : `
    background: transparent;
    color: #94a3b8;
    &:hover {
      background: #f8fafc;
      color: #64748b;
    }
  `}
`;

const ViewToggleContainer = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  display: flex;
  gap: 4px;
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  ${props => props.$active ? `
    background: white;
    color: var(--accent);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
    color: #64748b;
    &:hover {
      background: rgba(255, 255, 255, 0.5);
      color: #475569;
    }
  `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const ListContainer = styled.div`
  height: 100%;
  background: #f7f8fa;
  padding: 24px 40px 40px 40px;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 16px 100px 16px;
  }
`;

const ListHeader = styled.div`
  margin-bottom: 32px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 32px 9px 36px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #374151;
  font-size: 13.5px;
  font-weight: 500;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder { color: #94a3b8; font-weight: 400; }
  &:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(147,197,253,0.25); }

  @media (max-width: 768px) { min-width: 0; width: 100%; }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: color 0.2s ease;
  border-radius: 50%;

  &:hover {
    color: #64748b;
    background: #f1f5f9;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ListTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const ListSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
`;

const PageHeader = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const PageTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PageTitleText = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PageTitleCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  border-radius: 20px;
  padding: 3px 10px;
`;

const RegionPickerWrapper = styled.div`
  position: relative;
`;

const RegionPickerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.3)' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$active ? 'var(--accent)' : '#475569'};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(var(--accent-rgb), 0.08);
  }

  .region-label-short { display: none; }

  @media (max-width: 768px) {
    .region-label-full { display: none; }
    .region-label-short { display: inline; }
  }
`;

const RegionDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 240px;
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const RegionDropdownTabs = styled.div`
  display: flex;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
`;

const RegionDropdownTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 6px 8px;
  border: none;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  color: ${p => p.$active ? 'var(--accent)' : '#94a3b8'};

  &:hover {
    background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : '#f1f5f9'};
    color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  }
`;

const RegionDropdownSearch = styled.div`
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
`;

const RegionDropdownList = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const RegionOption = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$active ? 'var(--accent)' : '#334155'};
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  transition: background 0.12s;

  &:hover {
    background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : '#f8fafc'};
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  ${Table} {
    box-shadow: none;
    border-radius: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// --- Mobile list view ---
const MobileList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const MobileCard = styled.div<{ $hasResponsible?: boolean }>`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
  &:active { box-shadow: 0 2px 8px rgba(59,130,246,0.1); }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
`;

const MobileCardCityName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileCardMenuBtn = styled.button`
  background: none;
  border: none;
  padding: 0 2px;
  color: #94a3b8;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  letter-spacing: 1px;
`;

const MobileCardSubtitle = styled.div`
  font-size: 12.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-bottom: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileCardDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 10px;
`;

const MobileCardResponsibleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const MobileCardResponsibleName = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: #334155;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const MobileCardNoResponsible = styled.span`
  font-size: 13px;
  color: #cbd5e1;
  font-style: italic;
`;

const MobileCardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MobileCardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const MobileCardMetaLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  flex-wrap: wrap;
`;

const MobileCardMetaLabel = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const MobileCardMetaVotesNum = styled.span<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  font-size: 17px;
  font-weight: 700;
  color: ${({ $color }) =>
    $color === 'green' ? '#059669' :
    $color === 'yellow' ? '#d97706' :
    $color === 'red' ? '#dc2626' : '#334155'};
`;

const MobileCardMetaGoal = styled.span`
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
`;

const MobileCardMetaPercBadge = styled.span<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${({ $color }) =>
    $color === 'green' ? '#dcfce7' :
    $color === 'yellow' ? '#fef9c3' :
    $color === 'red' ? '#fee2e2' : '#f1f5f9'};
  color: ${({ $color }) =>
    $color === 'green' ? '#059669' :
    $color === 'yellow' ? '#d97706' :
    $color === 'red' ? '#dc2626' : '#64748b'};
`;

const MobileCardFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 4px;
`;

const MobileCardBottomRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

const MobileCardBottomLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
`;

const MobileCardBottomRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  flex-shrink: 0;
  width: 110px;
`;

const MobileCardLastVotes = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
`;

const MobileCardChangeBadge = styled.span<{ $up: boolean }>`
  font-size: 11.5px;
  font-weight: 700;
  color: ${p => p.$up ? '#059669' : '#dc2626'};
`;

const MobileShimmerCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
`;

const ElectionVoteCell = styled.div<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

const ElectionVotesNumber = styled.span<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  font-size: 14px;
  font-weight: 700;
  color: ${
    ({ $color }) =>
      $color === 'green' ? '#059669' :
      $color === 'yellow' ? '#d97706' :
      $color === 'red' ? '#dc2626' :
      '#64748b'
  };
`;

const ElectionMetaNumber = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

const TableHead = styled.thead`
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
`;

const TableHeaderCell = styled.th<{ $sortable?: boolean; $narrow?: boolean }>`
  padding: 16px 20px;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  user-select: none;
  white-space: nowrap;
  ${props => props.$narrow && 'width: 1%; padding-left: 12px; padding-right: 12px;'}

  &:hover {
    ${props => props.$sortable && `
      background: #f1f5f9;
    `}
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td<{ $allowOverflow?: boolean }>`
  padding: 16px 20px;
  font-size: 14px;
  color: #1e293b;
  ${props => props.$allowOverflow && `
    position: relative;
    overflow: visible;
  `}
`;

const CityName = styled.div`
  font-weight: 600;
  color: #1e293b;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CityUF = styled.span`
  color: #64748b;
  font-size: 13px;
  margin-left: 8px;
`;

const ResponsibleBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.25);
  border-radius: 6px;
  font-size: 13px;
  color: var(--accent);
`;

const ResponsibleCell = styled.div<{ $isAdmin?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.1s;
  cursor: ${props => props.$isAdmin ? 'pointer' : 'default'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;

  ${props => props.$isAdmin && `
    &:hover {
      background: #f1f5f9;
    }
  `}
`;

const ResponsibleAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ResponsiblePlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px dashed #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #94a3b8;
`;

const ResponsibleName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

const ResponsibleDropdown = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 250px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const DropdownOptionsList = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const ResponsibleOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.08s;
  will-change: background;
  contain: layout style;

  &:hover {
    background: #f8fafc;
  }

  &:first-child {
    border-radius: 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const OptionAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OptionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const OptionRole = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const UnassignedText = styled.span`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
`;

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinnerCircle = styled.span`
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 2px solid rgba(var(--accent-rgb), 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: ${spinAnimation} 0.7s linear infinite;
  flex-shrink: 0;
`;

// Shimmer components
const shimmerAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

const ShimmerRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
`;

const ShimmerCell = styled.td`
  padding: 16px 20px;
`;

const ShimmerBox = styled.div<{ $width?: string; $height?: string }>`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '16px'};
  background: linear-gradient(
    90deg,
    #f1f5f9 0%,
    #e2e8f0 50%,
    #f1f5f9 100%
  );
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
  border-radius: 4px;
  
  ${shimmerAnimation}
`;

const ShimmerBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: linear-gradient(
    90deg,
    #f1f5f9 0%,
    #e2e8f0 50%,
    #f1f5f9 100%
  );
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
  border-radius: 16px;
  width: 120px;
  height: 32px;
  
  ${shimmerAnimation}
`;

const DropdownShimmerOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const DropdownShimmerAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 1000px 100%;
  animation: shimmer 1.4s infinite linear;
  ${shimmerAnimation}
`;

const DropdownShimmerLine = styled.div<{ $width?: string }>`
  height: 11px;
  width: ${p => p.$width || '120px'};
  border-radius: 4px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 1000px 100%;
  animation: shimmer 1.4s infinite linear;
  ${shimmerAnimation}
`;

const EditableInput = styled.input`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const EditableCell = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => props.$isEditing ? 'text' : 'pointer'};
  padding: 4px;
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.$isEditing ? 'transparent' : '#f8fafc'};
  }
`;

const SortIcon = styled.span<{ $direction?: 'asc' | 'desc' }>`
  display: inline-block;
  margin-left: 6px;
  opacity: ${props => props.$direction ? '1' : '0.3'};
`;

// Multiplicadores Cell Component
const MultiplicadoresNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  background: rgba(var(--accent-rgb), 0.08);
  color: var(--accent);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--accent-rgb), 0.15);
    transform: translateY(-1px);
  }
`;

const MultiplicadoresPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 8px;
  min-width: 200px;
  max-width: 280px;
  z-index: 1000;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 12px;
    background: white;
    border-left: 1px solid #e2e8f0;
    border-top: 1px solid #e2e8f0;
    transform: translateX(-50%) rotate(45deg);
  }
`;

const MultiplicadoresTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding: 0 4px;
`;

const MultiplicadoresList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const MultiplicadorItem = styled.div`
  padding: 6px 8px;
  font-size: 13px;
  color: #1e293b;
  border-radius: 6px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8fafc;
  }
`;

interface MultiplicadoresCellProps {
  count: number;
  multiplicadores: Array<{ id: number; nome: string }>;
}

const MultiplicadoresCell: React.FC<MultiplicadoresCellProps> = ({ count }) => {
  if (count === 0) {
    return <span style={{ color: '#cbd5e1', fontSize: 13 }}>-</span>;
  }

  return (
    <MultiplicadoresNumber>{count}</MultiplicadoresNumber>
  );
};

interface SparklineCellProps {
  history: ElectionResult[];
}

const SparklineCell: React.FC<SparklineCellProps> = ({ history }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!history || history.length === 0) {
    return <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
  }

  const allSorted = [...history].sort((a, b) => a.ano - b.ano);
  const lastEntry = allSorted[allSorted.length - 1];
  const isCurrentCampaign = lastEntry && !(lastEntry.votos_obtidos ?? 0);
  const sorted = isCurrentCampaign ? allSorted.slice(0, -1) : allSorted;
  const values = sorted.map(e => e.votos_obtidos ?? 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 130, H = 44, PAD = 8;

  const points = sorted.map((e, i) => ({
    x: PAD + (i / Math.max(sorted.length - 1, 1)) * (W - PAD * 2),
    y: H - PAD - (((e.votos_obtidos ?? 0) - min) / range) * (H - PAD * 2),
    entry: e,
  }));

  const polyline = points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  return (
    <SparklineWrapper
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
        <polyline points={polyline} fill="none" stroke="#93c5fd" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill="white" stroke="#3b82f6" strokeWidth="1.5" />
        ))}
        {sorted.length > 0 && (
          <>
            <text x={PAD} y={H - 1} fontSize="9" fill="#cbd5e1" textAnchor="middle">'{String(sorted[0].ano).slice(-2)}</text>
            <text x={W - PAD} y={H - 1} fontSize="9" fill="#cbd5e1" textAnchor="middle">'{String(sorted[sorted.length - 1].ano).slice(-2)}</text>
          </>
        )}
      </svg>
      {showTooltip && (
        <SparklineTooltip>
          {sorted.map(e => (
            <div key={e.ano}>{e.ano}: {(e.votos_obtidos ?? 0).toLocaleString('pt-BR')}</div>
          ))}
        </SparklineTooltip>
      )}
    </SparklineWrapper>
  );
};

interface CustomRegion {
  id: number;
  nome: string;
  cidade_ids: number[];
}

interface MapDashboardProps {
  onSideSheetChange?: (isOpen: boolean) => void;
  onDisplayModeChange?: (mode: 'map' | 'list') => void;
  displayMode?: 'map' | 'list';
  onNavVisibilityChange?: (visible: boolean) => void;
}

const MapDashboard: React.FC<MapDashboardProps> = ({ onSideSheetChange, onDisplayModeChange, displayMode: displayModeProp, onNavVisibilityChange }) => {
  const { session, isAdmin, userData } = useAuth();
  const [currentRegion] = useState<MapRegion>(REGIONS.RIO_GRANDE_DO_SUL);
  const lastListScrollY = React.useRef(0);
  const listScrollTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    // Infinite scroll: load more cities when near bottom
    if (!cityLoadingMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 400) {
      if (cityVisibleCount < filteredAndSortedCities.length) {
        setCityLoadingMore(true);
        setTimeout(() => {
          setCityVisibleCount(prev => prev + 50);
          setCityLoadingMore(false);
        }, 150);
      }
    }
    if (!onNavVisibilityChange) return;
    const currentScrollY = el.scrollTop;
    if (listScrollTimeout.current) {
      clearTimeout(listScrollTimeout.current);
      listScrollTimeout.current = null;
    }
    if (currentScrollY > lastListScrollY.current && currentScrollY > 80) {
      onNavVisibilityChange(false);
    } else if (currentScrollY < lastListScrollY.current) {
      onNavVisibilityChange(true);
    }
    listScrollTimeout.current = setTimeout(() => {
      onNavVisibilityChange(true);
    }, 1500);
    lastListScrollY.current = currentScrollY;
  };
  const [viewMode, setViewMode] = useState<'traditional' | 'custom'>('traditional');
  const [isRegionZoomed, setIsRegionZoomed] = useState(false);
  const { can, loading: permissionsLoading } = usePermissionCheck();
  
  const [displayModeInternal, setDisplayModeInternal] = useState<'map' | 'list'>(() => {
    const saved = localStorage.getItem('displayMode');
    return (saved === 'map' || saved === 'list') ? saved : 'list';
  });
  const displayMode = displayModeProp ?? displayModeInternal;
  const setDisplayMode = (mode: 'map' | 'list') => {
    setDisplayModeInternal(mode);
    localStorage.setItem('displayMode', mode);
    onDisplayModeChange?.(mode);
  };
  const [customRegions, setCustomRegions] = useState<CustomRegion[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [citiesData, setCitiesData] = useState<CityData[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [triggerCityOpen, setTriggerCityOpen] = useState<{id: number, nome: string} | null>(null);
  
  // Dropdown state for responsável assignment
  const [openResponsibleDropdown, setOpenResponsibleDropdown] = useState<number | null>(null);
  const openResponsibleDropdownRef = React.useRef<number | null>(null);
  const [assigningResponsible, setAssigningResponsible] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [responsibleSearch, setResponsibleSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const regionPickerRef = useRef<HTMLDivElement>(null);

  // Stat card filters
  const [filterMetReached, setFilterMetReached] = useState(false);
  const [filterInProgress, setFilterInProgress] = useState(false);
  const [filterNoResponsible, setFilterNoResponsible] = useState(false);

  // Chip filters
  const [responsibleFilter, setResponsibleFilter] = useState('all');
  const [performanceFilter, setPerformanceFilter] = useState('all');
  const [showResponsibleFilter, setShowResponsibleFilter] = useState(false);
  const [showPerformanceFilter, setShowPerformanceFilter] = useState(false);
  const [responsibleFilterSearch, setResponsibleFilterSearch] = useState('');
  const [showMobileFilterSheet, setShowMobileFilterSheet] = useState(false);
  const [mobileRegionSearch, setMobileRegionSearch] = useState('');
  const mapRegionPickerRef = useRef<HTMLDivElement>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{
    id: number;
    nome: string;
    tipo_usuario: string;
    nivel_acesso: string;
    foto_url?: string | null;
  }>>([]); 
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(true);

  // Inline editing state for projection
  const [editingProjection, setEditingProjection] = useState<number | null>(null);
  const [projectionValue, setProjectionValue] = useState<string>('');

  // City detail modal state
  const [selectedCityForModal, setSelectedCityForModal] = useState<CityData | null>(null);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [cityModalRegistros, setCityModalRegistros] = useState<CityModalRegistro[]>([]);
  const [cityModalDemandas, setCityModalDemandas] = useState<CityModalDemanda[]>([]);
  const [cityModalContatos, setCityModalContatos] = useState<CityModalContato[]>([]);
  const [loadingCityModalData, setLoadingCityModalData] = useState(false);
  const [isNewDemandFromCityOpen, setIsNewDemandFromCityOpen] = useState(false);
  const [newDemandCityId, setNewDemandCityId] = useState<number | null>(null);

  // Demand detail modal (opened from city modal)
  const [selectedDemandForDetail, setSelectedDemandForDetail] = useState<CityModalDemanda | null>(null);
  const [isDemandDetailFromCityOpen, setIsDemandDetailFromCityOpen] = useState(false);
  const [demandDetailRegistros, setDemandDetailRegistros] = useState<any[]>([]);
  const [loadingDemandDetailRegistros, setLoadingDemandDetailRegistros] = useState(false);
  const [demandDetailHistorico, setDemandDetailHistorico] = useState<any[]>([]);
  const [loadingDemandDetailHistorico, setLoadingDemandDetailHistorico] = useState(false);
  const [demandDetailNewRegistro, setDemandDetailNewRegistro] = useState('');
  const [demandDetailAddingRegistro, setDemandDetailAddingRegistro] = useState(false);
  const [demandDetailEditingRegistro, setDemandDetailEditingRegistro] = useState<number | null>(null);
  const [demandDetailEditingText, setDemandDetailEditingText] = useState('');
  const [demandDetailOpenMenuId, setDemandDetailOpenMenuId] = useState<number | null>(null);
  const [demandDetailSavingRegistroId, setDemandDetailSavingRegistroId] = useState<number | null>(null);
  const [demandDetailDeletingRegistroId, setDemandDetailDeletingRegistroId] = useState<number | null>(null);

  useEffect(() => {
    if (session?.access_token && viewMode === 'custom') {
      fetchCustomRegions();
    }
  }, [session, viewMode]);

  useEffect(() => {
    if (session?.access_token && !permissionsLoading) {
      fetchCitiesData();
      fetchTeamMembers();
    }
  }, [session, permissionsLoading]);

  // Limpar trigger após ser processado
  useEffect(() => {
    if (triggerCityOpen) {
      const timer = setTimeout(() => {
        setTriggerCityOpen(null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [triggerCityOpen]);

  // Limpar filtros ao voltar para o modo de mapa
  useEffect(() => {
    if (displayMode === 'map') {
      setSelectedRegionFilter('');
    }
  }, [displayMode]);

  // Fechar region picker ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        regionPickerRef.current && !regionPickerRef.current.contains(target) &&
        mapRegionPickerRef.current && !mapRegionPickerRef.current.contains(target)
      ) {
        setShowRegionPicker(false);
        setRegionSearch('');
      }
      // Close responsible dropdown if clicking outside
      if (openResponsibleDropdownRef.current && !target.closest('[data-dropdown]')) {
        openResponsibleDropdownRef.current = null;
        setOpenResponsibleDropdown(null);
        setDropdownPosition(null);
      }
      // Close chip filter dropdowns
      if (!target.closest('[data-city-filter]')) {
        setShowResponsibleFilter(false);
        setShowPerformanceFilter(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openResponsibleDropdownRef.current !== null) {
        openResponsibleDropdownRef.current = null;
        setOpenResponsibleDropdown(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const fetchCustomRegions = async () => {
    if (!session?.access_token) return;

    setLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCustomRegions(result.data || []);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar regiões customizadas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCitiesData = async () => {
    if (!session?.access_token) return;

    setLoadingCities(true);
    try {
      // Buscar todas as cidades do RS
      const rsCities = (citiesWithCoordinates as any[]).filter(
        city => city.microrregiao?.mesorregiao?.UF?.sigla === 'RS'
      );

      // Prepare city IDs for batch requests
      const cityIds = rsCities.map((city: any) => city.id).join(',');

      // Fetch enrichment data in parallel
      const [electionHistoryRes, responsaveisRes, multiplicadoresRes, populacaoRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/v1/cidades/election-history`,
          { headers: { 'Authorization': `Bearer ${session.access_token}` } }
        ),
        fetch(
          `${API_BASE_URL}/api/v1/cidades/batch-responsaveis?ids=${cityIds}`,
          { headers: { 'Authorization': `Bearer ${session.access_token}` } }
        ),
        fetch(
          `${API_BASE_URL}/api/v1/cidades/batch-multiplicadores?ids=${cityIds}`,
          { headers: { 'Authorization': `Bearer ${session.access_token}` } }
        ),
        fetch(
          `${API_BASE_URL}/api/v1/cidades/batch-populacao?ids=${cityIds}`,
          { headers: { 'Authorization': `Bearer ${session.access_token}` } }
        ),
      ]);

      const electionHistory = electionHistoryRes.ok ? (await electionHistoryRes.json()).data : {};
      const responsaveis = responsaveisRes.ok ? (await responsaveisRes.json()).data : {};
      const multiplicadores = multiplicadoresRes.ok ? (await multiplicadoresRes.json()).data : {};
      const populacaoData: Record<number, { total: number; votantes: number; ano: number | null }> =
        populacaoRes.ok ? (await populacaoRes.json()).data : {};

      // Merge all data
      const currentYear = new Date().getFullYear();
      let citiesWithData: CityData[] = rsCities.map((city: any) => {
        const cityHistory: any[] = electionHistory[city.id] || [];
        const cityMultiplicadores = multiplicadores[city.id] || { count: 0, multiplicadores: [] };
        // Use meta_votos from the closest upcoming election that has a target set
        const nextEntry = cityHistory
          .filter((e: any) => e.ano >= currentYear && e.meta_votos != null)
          .sort((a: any, b: any) => a.ano - b.ano)[0];
        return {
          id: city.id,
          nome: city.nome,
          uf: city.microrregiao?.mesorregiao?.UF?.sigla || 'RS',
          populacao: populacaoData[city.id]?.total || city.population || 0,
          populacao_votantes: populacaoData[city.id]?.votantes || 0,
          populacao_ano: populacaoData[city.id]?.ano || null,
          responsavel: responsaveis[city.id] || null,
          num_demandas: 0,
          num_multiplicadores: cityMultiplicadores.count,
          multiplicadores: cityMultiplicadores.multiplicadores,
          projecao_votos: nextEntry?.meta_votos ?? undefined,
          mesorregiao: city.microrregiao?.mesorregiao?.nome || '',
          electionHistory: cityHistory
        };
      });

      // If user only has scoped view, filter to cities where they are the responsavel
      if (!can('cities.view.all') && can('cities.view.scoped')) {
        citiesWithData = citiesWithData.filter(
          city => city.responsavel?.id === userData?.id
        );
      }

      setCitiesData(citiesWithData);
    } catch (error) {
      console.error('Erro ao buscar dados das cidades:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!session?.access_token) return;
    setLoadingTeamMembers(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setTeamMembers(result.data);
          // Preload avatar images to prevent lag when opening dropdown
          result.data.forEach((member: { id: number }) => {
            const img = new Image();
            img.src = getPhotoUrl(member.id);
          });
        }
      }
    } catch (error) {
      console.error('Erro ao buscar membros do time:', error);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleResponsibleClick = (e: React.MouseEvent, cityId: number) => {
    if (!isAdmin) return;
    e.stopPropagation();
    setResponsibleSearch('');
    if (openResponsibleDropdownRef.current === cityId) {
      openResponsibleDropdownRef.current = null;
      setOpenResponsibleDropdown(null);
      setDropdownPosition(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
      openResponsibleDropdownRef.current = cityId;
      setOpenResponsibleDropdown(cityId);
    }
  };

  const handleAssignResponsible = async (cityId: number, userId: number) => {
    if (!session?.access_token) return;

    setAssigningResponsible(cityId);
    openResponsibleDropdownRef.current = null;
    setOpenResponsibleDropdown(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${cityId}/responsavel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ responsavel_id: userId }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Atualizar apenas a cidade específica no estado local
        setCitiesData(prevCities => 
          prevCities.map(city => 
            city.id === cityId 
              ? { ...city, responsavel: result.data }
              : city
          )
        );
        // Atualizar a modal se estiver aberta para essa cidade
        setSelectedCityForModal(prev => 
          prev?.id === cityId ? { ...prev, responsavel: result.data } : prev
        );
      } else {
        alert('Erro ao atribuir responsável');
      }
    } catch (error) {
      console.error('Erro ao atribuir responsável:', error);
      alert('Erro ao atribuir responsável');
    } finally {
      setAssigningResponsible(null);
    }
  };

  const getPhotoUrl = (id: number) => {
    const avatarId = (id % 70) + 1;
    return `https://i.pravatar.cc/150?img=${avatarId}`;
  };

  const handleProjectionClick = (city: CityData, e: React.MouseEvent) => {
    if (!isAdmin) return;
    e.stopPropagation();
    setEditingProjection(city.id);
    setProjectionValue(city.projecao_votos?.toString() || '');
  };

  const handleProjectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    setProjectionValue(value);
  };

  const handleProjectionSave = async (cityId: number) => {
    if (!session?.access_token || !projectionValue) {
      setEditingProjection(null);
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${cityId}/projecao`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projecao_votos: parseInt(projectionValue) }),
      });

      if (response.ok) {
        // Update local state
        setCitiesData(prevCities =>
          prevCities.map(city =>
            city.id === cityId
              ? { ...city, projecao_votos: parseInt(projectionValue) }
              : city
          )
        );
      } else {
        alert('Erro ao salvar projeção');
      }
    } catch (error) {
      console.error('Erro ao salvar projeção:', error);
      alert('Erro ao salvar projeção');
    } finally {
      setEditingProjection(null);
      setProjectionValue('');
    }
  };

  const handleProjectionKeyDown = (e: React.KeyboardEvent, cityId: number) => {
    if (e.key === 'Enter') {
      handleProjectionSave(cityId);
    } else if (e.key === 'Escape') {
      setEditingProjection(null);
      setProjectionValue('');
    }
  };

  const handleElectionDataSave = async (cityId: number, year: number, data: { votos_obtidos: number; meta_votos: number | null }) => {
    if (!session?.access_token) return;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${cityId}/election-result`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          ano: year,
          votos_obtidos: data.votos_obtidos,
          meta_votos: data.meta_votos
        }),
      });

      if (response.ok) {
        // Recalculate percentage and status
        const percentage = data.meta_votos 
          ? (data.votos_obtidos / data.meta_votos) * 100
          : null;
        
        const status = percentage === null ? 'neutral'
          : percentage >= 100 ? 'success'
          : percentage >= 80 ? 'warning'
          : 'danger';

        // Update local state
        setCitiesData(prevCities =>
          prevCities.map(city =>
            city.id === cityId
              ? {
                  ...city,
                  electionHistory: (city.electionHistory || []).map(e =>
                    e.ano === year
                      ? { ...e, votos_obtidos: data.votos_obtidos, meta_votos: data.meta_votos, percentage, status }
                      : e
                  )
                }
              : city
          )
        );
      } else {
        alert('Erro ao salvar dados da eleição');
      }
    } catch (error) {
      console.error('Erro ao salvar dados da eleição:', error);
      alert('Erro ao salvar dados da eleição');
    }
  };

  // Converter regiões customizadas em GeoJSON
  const customRegionsGeoJSON = React.useMemo(() => {
    if (viewMode !== 'custom' || customRegions.length === 0) {
      return null;
    }

    const features = customRegions.map((region, index) => {
      // Encontrar as cidades da região
      const cities = region.cidade_ids
        .map(cityId => {
          const city = citiesWithCoordinates.find((c: any) => c.id === cityId);
          return city;
        })
        .filter(Boolean);

      if (cities.length === 0) return null;

      // Criar um polígono simples usando convex hull das coordenadas das cidades
      const coordinates = cities.map((city: any) => [city.longitude, city.latitude]);
      
      // Calcular convex hull simplificado (ordenar por ângulo do centro)
      const center = {
        lng: coordinates.reduce((sum, c) => sum + c[0], 0) / coordinates.length,
        lat: coordinates.reduce((sum, c) => sum + c[1], 0) / coordinates.length
      };

      const sortedCoords = [...coordinates].sort((a, b) => {
        const angleA = Math.atan2(a[1] - center.lat, a[0] - center.lng);
        const angleB = Math.atan2(b[1] - center.lat, b[0] - center.lng);
        return angleA - angleB;
      });

      // Adicionar margem aos pontos (buffer simples)
      const bufferedCoords = sortedCoords.map(([lng, lat]) => {
        const dx = lng - center.lng;
        const dy = lat - center.lat;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const buffer = 0.15; // ~15km de buffer
        return [
          lng + (dx / distance) * buffer,
          lat + (dy / distance) * buffer
        ];
      });

      // Fechar o polígono
      bufferedCoords.push(bufferedCoords[0]);

      return {
        type: 'Feature',
        properties: {
          mesorregiao_nome: region.nome,
          nome: region.nome,
          custom_region_id: region.id,
          custom_region_index: index,
          cidade_ids: region.cidade_ids
        },
        geometry: {
          type: 'Polygon',
          coordinates: [bufferedCoords]
        }
      };
    }).filter(Boolean);

    return {
      type: 'FeatureCollection',
      features
    };
  }, [customRegions, viewMode]);

  const handleCityClick = useCallback((cityName: string, properties: any) => {
    // Abrir modal de cidade
    let cityData = citiesData.find(c => c.nome === cityName || c.id === properties.id);
    if (!cityData) {
      // Fallback: construct minimal CityData from map marker properties
      cityData = {
        id: properties.id,
        nome: cityName,
        uf: 'RS',
        populacao: properties.population || 0,
        populacao_votantes: 0,
        populacao_ano: null,
        mesorregiao: properties.mesorregiao || '',
        electionHistory: [],
      };
    }
    fetchCityModalData(cityData);
  }, [citiesData, session]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCityModalData = async (city: CityData) => {
    if (!session?.access_token) return;
    setSelectedCityForModal(city);
    setIsCityModalOpen(true);
    setCityModalRegistros([]);
    setCityModalDemandas([]);
    setCityModalContatos([]);
    setLoadingCityModalData(true);
    try {
      const [demandasRes, comentariosRes, contatosRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/demandas?cidade_id=${city.id}&limit=50`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/cidades/${city.id}/comentarios`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/eleitores?cidade_id=${city.id}&limit=200`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
      ]);
      if (demandasRes.ok) {
        const json = await demandasRes.json();
        const list = Array.isArray(json) ? json : (json.data ?? []);
        setCityModalDemandas(list.map((d: any) => ({
          id: d.id,
          titulo: d.titulo,
          status: d.status,
          data_cadastro: d.data_cadastro ?? d.created_at,
          solicitante: d.solicitante ?? undefined,
        })));
      }
      if (comentariosRes.ok) {
        const json = await comentariosRes.json();
        const list = Array.isArray(json) ? json : (json.data ?? []);
        setCityModalRegistros(list.map((c: any) => ({
          id: c.id,
          texto: c.texto,
          tipo: 'comentario' as const,
          created_at: c.created_at,
          autor: c.usuario ? { id: c.usuario.id, nome: c.usuario.nome } : undefined,
        })));
      }
      if (contatosRes.ok) {
        const json = await contatosRes.json();
        const list = Array.isArray(json) ? json : (json.data ?? []);
        setCityModalContatos(list.map((c: any) => ({
          id: c.id,
          nome: c.nome,
          cargo: c.profissao ?? undefined,
          foto_url: c.foto_url ?? null,
          is_multiplicador: c.multiplicador ?? false,
          whatsapp: c.telefone ?? undefined,
        })));
      }
    } catch (err) {
      console.error('Erro ao buscar dados do modal da cidade:', err);
    } finally {
      setLoadingCityModalData(false);
    }
  };

  const handleAddCityComment = async (cityId: number, texto: string) => {
    if (!session?.access_token) return;
    const res = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${cityId}/comentarios`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ texto }),
    });
    if (res.ok) {
      const json = await res.json();
      const c = json.data;
      setCityModalRegistros(prev => [{
        id: c.id,
        texto: c.texto,
        tipo: 'comentario' as const,
        created_at: c.created_at,
        autor: c.usuario ? { id: c.usuario.id, nome: c.usuario.nome } : undefined,
      }, ...prev]);
    }
  };

  const handleDemandClickFromCity = async (demand: CityModalDemanda) => {
    setSelectedDemandForDetail(demand);
    setIsDemandDetailFromCityOpen(true);
    setDemandDetailRegistros([]);
    setDemandDetailHistorico([]);
    if (!session?.access_token) return;
    setLoadingDemandDetailRegistros(true);
    setLoadingDemandDetailHistorico(true);
    try {
      const [regRes, histRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/demandas/${demand.id}/registros`, { headers: { Authorization: `Bearer ${session.access_token}` } }),
        apiFetch(`${API_BASE_URL}/api/v1/demandas/${demand.id}/historico`, { headers: { Authorization: `Bearer ${session.access_token}` } }),
      ]);
      if (regRes.ok) { const j = await regRes.json(); setDemandDetailRegistros(j.data ?? j ?? []); }
      if (histRes.ok) { const j = await histRes.json(); setDemandDetailHistorico(j.data ?? j ?? []); }
    } finally {
      setLoadingDemandDetailRegistros(false);
      setLoadingDemandDetailHistorico(false);
    }
  };

  const handleDemandDetailFromCityClose = () => {
    setIsDemandDetailFromCityOpen(false);
    setSelectedDemandForDetail(null);
    setDemandDetailRegistros([]);
    setDemandDetailHistorico([]);
    setDemandDetailNewRegistro('');
    setDemandDetailEditingRegistro(null);
    setDemandDetailEditingText('');
    setDemandDetailOpenMenuId(null);
  };

  const handleDemandUpdatedFromCity = async (demandId: number) => {
    if (!session?.access_token) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demandId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      const d = json.data ?? json;
      setCityModalDemandas(prev =>
        prev.map(x =>
          x.id === demandId
            ? { ...x, titulo: d.titulo, status: d.status, solicitante: d.solicitante ?? x.solicitante }
            : x
        )
      );
    } catch {}
  };

  const handleDemandDetailAddRegistro = async () => {
    if (!selectedDemandForDetail || !demandDetailNewRegistro.trim() || !session?.access_token) return;
    setDemandDetailAddingRegistro(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${selectedDemandForDetail.id}/registros`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ descricao_atualizacao: demandDetailNewRegistro }),
      });
      if (res.ok) {
        const j = await res.json();
        setDemandDetailRegistros(prev => [j.data ?? j, ...prev]);
        setDemandDetailNewRegistro('');
      }
    } finally {
      setDemandDetailAddingRegistro(false);
    }
  };

  const handleNewDemandFromCity = (cityId: number) => {
    setNewDemandCityId(cityId);
    setIsNewDemandFromCityOpen(true);
  };

  const handleDemandFromCitySubmit = async (formData: DemandFormData) => {
    if (!session?.access_token) return;
    const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });
    if (res.ok) {
      const json = await res.json();
      const d = json.data ?? json;
      // Append to the city modal demandas list optimistically
      if (d && newDemandCityId) {
        setCityModalDemandas(prev => [{
          id: d.id,
          titulo: d.titulo,
          status: d.status,
          data_cadastro: d.data_cadastro ?? d.created_at,
          solicitante: d.solicitante ?? undefined,
        }, ...prev]);
      }
      setIsNewDemandFromCityOpen(false);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(`Erro ao criar demanda: ${err.error || 'Erro desconhecido'}`);
    }
  };

  const handleRowClick = (city: CityData) => {
    fetchCityModalData(city);
  };

  const handleSaveCityData = async (cityId: number, data: {
    electionHistory?: ElectionResult[];
    projecao_votos?: number;
    deletedElectionYears?: number[];
    notas_estrategicas?: string;
  }) => {
    if (!session?.access_token) return;

    try {
      // Deletar eleições removidas pelo usuário
      if (data.deletedElectionYears?.length) {
        for (const ano of data.deletedElectionYears) {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/cidades/${cityId}/election-result/${ano}`,
            {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${session.access_token}` }
            }
          );
          if (!response.ok) throw new Error(`Erro ao deletar eleição ${ano}`);
        }
      }

      // Salvar resultados eleitorais
      if (data.electionHistory) {
        for (const election of data.electionHistory) {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/cidades/${cityId}/election-result`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                ano: election.ano,
                votos_obtidos: election.votos_obtidos,
                meta_votos: election.meta_votos
              })
            }
          );
          if (!response.ok) throw new Error(`Erro ao salvar eleição ${election.ano}`);
        }
      }

      // Salvar notas estratégicas
      if (data.notas_estrategicas !== undefined) {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/cidades/${cityId}/notas`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ notas_estrategicas: data.notas_estrategicas })
          }
        );
        if (!response.ok) console.warn('Erro ao salvar notas estratégicas');
      }

      // Atualizar apenas a cidade afetada no estado local (sem refetch global)
      setCitiesData(prev => prev.map(city => {
        if (city.id !== cityId) return city;

        const updatedHistory = data.electionHistory
          ? data.electionHistory.map(e => ({
              ano: e.ano,
              votos_obtidos: e.votos_obtidos,
              meta_votos: e.meta_votos,
            }))
          : city.electionHistory;

        // Recalculate projecao_votos from next election meta
        const currentYear = new Date().getFullYear();
        const nextEntry = (updatedHistory ?? [])
          .filter((e: any) => e.ano >= currentYear && e.meta_votos != null)
          .sort((a: any, b: any) => a.ano - b.ano)[0];

        return {
          ...city,
          electionHistory: updatedHistory,
          projecao_votos: nextEntry?.meta_votos ?? data.projecao_votos ?? city.projecao_votos,
          ...(data.notas_estrategicas !== undefined && { notas_estrategicas: data.notas_estrategicas }),
        };
      }));

      // Also patch selectedCityForModal if it's the same city
      setSelectedCityForModal(prev =>
        prev?.id === cityId
          ? {
              ...prev,
              electionHistory: data.electionHistory ?? prev.electionHistory,
              ...(data.notas_estrategicas !== undefined && { notas_estrategicas: data.notas_estrategicas }),
            }
          : prev
      );

    } catch (error) {
      console.error('Erro ao salvar dados da cidade:', error);
      throw error;
    }
  };

  const regionProjections = React.useMemo(() => {
    if (viewMode === 'custom') {
      // Agrupar por regiões customizadas
      return customRegions.reduce((acc, region) => {
        const total = citiesData
          .filter(city => region.cidade_ids.includes(city.id) && city.projecao_votos != null)
          .reduce((sum, city) => sum + (city.projecao_votos ?? 0), 0);
        if (total > 0) {
          acc[region.nome] = total;
        }
        return acc;
      }, {} as Record<string, number>);
    }
    // Agrupar por mesorregiões tradicionais
    return citiesData.reduce((acc, city) => {
      if (city.mesorregiao && city.projecao_votos != null) {
        acc[city.mesorregiao] = (acc[city.mesorregiao] || 0) + city.projecao_votos;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [citiesData, viewMode, customRegions]);

  // Deduplicated grand total: each city counted only once, regardless of how many
  // custom regions it belongs to. Traditional mode already deduplicates naturally.
  const totalProjection = React.useMemo(() => {
    if (viewMode !== 'custom') return undefined;
    const seenIds = new Set<number>();
    let total = 0;
    for (const region of customRegions) {
      for (const cityId of region.cidade_ids) {
        if (seenIds.has(cityId)) continue;
        seenIds.add(cityId);
        const city = citiesData.find(c => c.id === cityId);
        if (city?.projecao_votos != null) total += city.projecao_votos;
      }
    }
    return total > 0 ? total : undefined;
  }, [citiesData, viewMode, customRegions]);

  const nextElectionYear = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = citiesData
      .flatMap(c => c.electionHistory || [])
      .filter(e => e.ano >= currentYear && e.meta_votos != null)
      .map(e => e.ano);
    return years.length > 0 ? Math.min(...years) : null;
  }, [citiesData]);

  const handleSideSheetChange = useCallback((isOpen: boolean) => {
    setIsSideSheetOpen(isOpen);
    if (onSideSheetChange) {
      onSideSheetChange(isOpen);
    }
  }, [onSideSheetChange]);

  const handleRegionZoomChange = useCallback((isZoomed: boolean) => {
    setIsRegionZoomed(isZoomed);
  }, []);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filtrar e ordenar cidades
  const allElectionYears = React.useMemo(() => {
    const yearsSet = new Set<number>();
    citiesData.forEach(city => {
      city.electionHistory?.forEach(e => yearsSet.add(e.ano));
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [citiesData]);

  const cityStats = React.useMemo(() => {
    const total = citiesData.length;
    const noResponsible = citiesData.filter(c => !c.responsavel).length;
    let metReached = 0;
    let inProgress = 0;
    citiesData.forEach(city => {
      const latestEntry = (city.electionHistory || []).sort((a, b) => b.ano - a.ano)[0];
      if (!latestEntry || latestEntry.votos_obtidos == null || !latestEntry.meta_votos) return;
      const pct = (latestEntry.votos_obtidos / latestEntry.meta_votos) * 100;
      if (pct >= 100) metReached++; else inProgress++;
    });
    return { total, noResponsible, metReached, inProgress };
  }, [citiesData]);

  const [cityVisibleCount, setCityVisibleCount] = useState(50);
  const [cityLoadingMore, setCityLoadingMore] = useState(false);

  const filteredAndSortedCities = React.useMemo(() => {
    let filtered = [...citiesData];

    // Filtrar por região
    if (selectedRegionFilter) {
      if (viewMode === 'custom') {
        // Filtrar por região customizada
        const region = customRegions.find(r => r.id.toString() === selectedRegionFilter);
        if (region) {
          filtered = filtered.filter(city => region.cidade_ids.includes(city.id));
        }
      } else {
        // Filtrar por mesorregião tradicional
        filtered = filtered.filter(city => city.mesorregiao === selectedRegionFilter);
      }
    }

    // Filtrar por busca de nome
    if (citySearch.trim()) {
      const searchLower = citySearch.toLowerCase().trim();
      filtered = filtered.filter(city =>
        city.nome.toLowerCase().includes(searchLower)
      );
    }

    // Stat card filters (AND logic)
    if (filterMetReached || filterInProgress) {
      filtered = filtered.filter(city => {
        const latest = (city.electionHistory || []).sort((a, b) => b.ano - a.ano)[0];
        if (!latest || latest.votos_obtidos == null || !latest.meta_votos) return false;
        const pct = (latest.votos_obtidos / latest.meta_votos) * 100;
        if (filterMetReached && !filterInProgress) return pct >= 100;
        if (filterInProgress && !filterMetReached) return pct < 100;
        return true;
      });
    }
    if (filterNoResponsible) filtered = filtered.filter(c => !c.responsavel);

    // Chip filters
    if (responsibleFilter !== 'all') {
      if (responsibleFilter === 'unassigned') filtered = filtered.filter(c => !c.responsavel);
      else filtered = filtered.filter(c => c.responsavel?.id?.toString() === responsibleFilter);
    }
    if (performanceFilter !== 'all') {
      filtered = filtered.filter(city => {
        const latest = (city.electionHistory || []).sort((a, b) => b.ano - a.ano)[0];
        if (performanceFilter === 'no_data') return !latest || latest.votos_obtidos == null;
        if (!latest || latest.votos_obtidos == null || !latest.meta_votos) return false;
        const pct = (latest.votos_obtidos / latest.meta_votos) * 100;
        if (performanceFilter === 'atingida') return pct >= 100;
        if (performanceFilter === 'em_progresso') return pct < 100;
        return true;
      });
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aVal: any = a[sortColumn as keyof CityData];
      let bVal: any = b[sortColumn as keyof CityData];

      // Tratar valores especiais
      if (sortColumn === 'responsavel') {
        aVal = a.responsavel?.nome || '';
        bVal = b.responsavel?.nome || '';
      } else if (sortColumn.startsWith('election_')) {
        const year = parseInt(sortColumn.replace('election_', ''));
        const aResult = a.electionHistory?.find(e => e.ano === year);
        const bResult = b.electionHistory?.find(e => e.ano === year);
        aVal = aResult?.votos_obtidos ?? -1;
        bVal = bResult?.votos_obtidos ?? -1;
      } else if (sortColumn === 'meta_2026') {
        aVal = (a.electionHistory || []).sort((x, y) => y.ano - x.ano)[0]?.votos_obtidos ?? -1;
        bVal = (b.electionHistory || []).sort((x, y) => y.ano - x.ano)[0]?.votos_obtidos ?? -1;
      }

      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [citiesData, selectedRegionFilter, sortColumn, sortDirection, viewMode, customRegions, citySearch, filterMetReached, filterInProgress, filterNoResponsible, responsibleFilter, performanceFilter]);

  const visibleCities = filteredAndSortedCities.slice(0, cityVisibleCount);

  const totalMeta2026 = React.useMemo(() => {
    return filteredAndSortedCities.reduce((sum, city) => {
      const sorted = [...(city.electionHistory || [])].sort((a, b) => b.ano - a.ano);
      const latest = sorted[0];
      return sum + (latest?.meta_votos ?? 0);
    }, 0);
  }, [filteredAndSortedCities]);

  useEffect(() => {
    setCityVisibleCount(50);
  }, [citySearch, selectedRegionFilter, filterMetReached, filterInProgress, filterNoResponsible, responsibleFilter, performanceFilter, sortColumn, sortDirection]);

  // Obter lista de regiões para o filtro
  const availableRegions = React.useMemo((): CustomRegion[] => {
    if (viewMode === 'custom') {
      return customRegions;
    } else {
      // Regiões tradicionais (mesorregiões)
      const mesorregioes = Array.from(new Set(citiesData.map(c => c.mesorregiao).filter(Boolean)));
      return mesorregioes.map((nome, index) => ({ id: index, nome: nome as string, cidade_ids: [] }));
    }
  }, [viewMode, customRegions, citiesData]);

  // Show shimmer while permissions or cities are still loading
  if (permissionsLoading || loadingCities) {
    const shimmerDisplayMode = (() => {
      const saved = localStorage.getItem('displayMode');
      return saved === 'map' ? 'map' : 'list';
    })();

    if (shimmerDisplayMode === 'map') {
      return (
        <PageContainer>
          <ContentArea>
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
              {/* Map shimmer: full grey background mimicking the map */}
              <ShimmerBox style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 0 }} />
              {/* Top-left title block */}
              <div style={{ position: 'absolute', top: 32, left: 40, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 110 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ShimmerBox $width="44px" $height="44px" style={{ borderRadius: 12 }} />
                  <ShimmerBox $width="120px" $height="28px" />
                </div>
                <ShimmerBox $width="240px" $height="16px" />
              </div>
              {/* Top-right toggle block */}
              <div style={{ position: 'absolute', top: 32, right: 40, zIndex: 20 }}>
                <ShimmerBox $width="240px" $height="44px" style={{ borderRadius: 12 }} />
              </div>
            </div>
          </ContentArea>
        </PageContainer>
      );
    }

    return (
      <PageContainer>
        <ContentArea>
          <ListContainer onScroll={handleListScroll}>
            <TitleSection>
              <PageTitleBlock>
                <PageTitleText>
                  Cidades
                </PageTitleText>
                <PageSubtitle>Visualize e gerencie todas as cidades do estado</PageSubtitle>
              </PageTitleBlock>
            </TitleSection>

            <CityStats>
              <CityStatCard $active={false} $accentColor="#3b82f6">
                <StatCardTop><StatCardLabel>TOTAL DE CIDADES</StatCardLabel></StatCardTop>
                <StatCardValue>–</StatCardValue>
                <StatCardSub>cobertura estadual</StatCardSub>
              </CityStatCard>
              <CityStatCard $active={false} $accentColor="#10b981">
                <StatCardTop><StatDot $color="#10b981"/><StatCardLabel>META 2026 ATINGIDA</StatCardLabel></StatCardTop>
                <StatCardValue>–</StatCardValue>
                <StatCardSub>/ – cidades</StatCardSub>
              </CityStatCard>
              <CityStatCard $active={false} $accentColor="#f59e0b">
                <StatCardTop><StatDot $color="#f59e0b"/><StatCardLabel>EM PROGRESSO</StatCardLabel></StatCardTop>
                <StatCardValue>–</StatCardValue>
                <StatCardSub>0–99% da meta</StatCardSub>
              </CityStatCard>
              <CityStatCard $active={false} $accentColor="#94a3b8">
                <StatCardTop><StatDot $color="#94a3b8"/><StatCardLabel>SEM RESPONSÁVEL</StatCardLabel></StatCardTop>
                <StatCardValue>–</StatCardValue>
                <StatCardSub>precisa atribuir</StatCardSub>
              </CityStatCard>
            </CityStats>

            <Controls>
              <SearchWrapper>
                <SearchIconWrap>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </SearchIconWrap>
                <SearchInput type="text" placeholder="Buscar cidade..." disabled value="" onChange={() => {}} />
              </SearchWrapper>
              <FilterGroup>
                <FilterChip type="button" $active={false} disabled>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  Região
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                </FilterChip>
                <FilterChip type="button" $active={false} disabled>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Responsável
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                </FilterChip>
                <FilterChip type="button" $active={false} disabled>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  Performance
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                </FilterChip>
              </FilterGroup>
            </Controls>

            <CityTable>
              <CityTableHeader $withElections={false}>
                <CityHeaderCell>CIDADE</CityHeaderCell>
                <CityHeaderCell>RESPONSÁVEL</CityHeaderCell>
                <CityHeaderCell>DEMANDAS</CityHeaderCell>
                <CityHeaderCell>MULTIPLIC.</CityHeaderCell>
                <CityHeaderCell>HISTÓRICO DE VOTOS</CityHeaderCell>
              </CityTableHeader>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(i => (
                <CityTableRow key={i} $hasResponsible={false} $withElections={false}>
                  <CityTableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '14px 0' }}>
                      <ShimmerBox $width="120px" $height="14px" />
                      <ShimmerBox $width="170px" $height="11px" />
                    </div>
                  </CityTableCell>
                  <CityTableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ShimmerBox $width="28px" $height="28px" style={{ borderRadius: '50%' }} />
                      <ShimmerBox $width="80px" $height="13px" />
                    </div>
                  </CityTableCell>
                  <CityTableCell><ShimmerBox $width="28px" $height="13px" /></CityTableCell>
                  <CityTableCell><ShimmerBox $width="28px" $height="13px" /></CityTableCell>
                  <CityTableCell><ShimmerBox $width="130px" $height="36px" /></CityTableCell>
                </CityTableRow>
              ))}
            </CityTable>
          </ListContainer>
        </ContentArea>
      </PageContainer>
    );
  }

  // Check permission only after loading is fully complete
  const hasAccess = can('cities.view.all') || can('cities.view.scoped');
  if (!hasAccess) {
    return (
      <PageContainer>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          gap: '16px',
          padding: '32px'
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', margin: 0 }}>Acesso Negado</h2>
          <p style={{ fontSize: '16px', color: '#64748b', textAlign: 'center', maxWidth: '400px', margin: 0 }}>
            Você não tem permissão para visualizar as cidades.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentArea>
        <div style={{ display: displayMode === 'map' ? 'block' : 'none', width: '100%', height: '100%', position: 'relative' }}>
          <MapTitleBlock>
            <MapTitleRow>
              <MapTitleIconBox>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </MapTitleIconBox>
              <MapTitleText>Cidades</MapTitleText>
            </MapTitleRow>
            <MapSubtitleText>Visualize e gerencie todas as cidades</MapSubtitleText>
            <MapTitleDividerLine />
          </MapTitleBlock>
          {!isRegionZoomed && (
            <MapToggleBlock>
              <RegionPickerWrapper ref={mapRegionPickerRef}>
                <RegionPickerButton
                  $active={!!selectedRegionFilter}
                  onClick={() => { setShowRegionPicker(p => !p); setRegionSearch(''); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M7 12h10M11 18h2"/>
                  </svg>
                  {selectedRegionFilter
                    ? availableRegions.find(r => String(viewMode === 'custom' ? r.id : r.nome) === selectedRegionFilter)?.nome
                    : <><span className="region-label-full">Filtrar por região</span><span className="region-label-short">Região</span></>}
                  <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
                </RegionPickerButton>
                {showRegionPicker && (
                  <RegionDropdown>
                    <RegionDropdownTabs>
                      <RegionDropdownTab
                        $active={viewMode === 'traditional'}
                        onClick={e => { e.stopPropagation(); setViewMode('traditional'); setSelectedRegionFilter(''); }}
                      >
                        Regiões Tradicionais
                      </RegionDropdownTab>
                      <RegionDropdownTab
                        $active={viewMode === 'custom'}
                        onClick={e => { e.stopPropagation(); setViewMode('custom'); setSelectedRegionFilter(''); }}
                      >
                        Minhas Regiões
                      </RegionDropdownTab>
                    </RegionDropdownTabs>
                    <RegionDropdownSearch>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Buscar região..."
                        value={regionSearch}
                        onChange={e => setRegionSearch(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          fontSize: 13,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </RegionDropdownSearch>
                    <RegionDropdownList>
                      <RegionOption
                        $active={!selectedRegionFilter}
                        onClick={() => { setSelectedRegionFilter(''); setShowRegionPicker(false); }}
                      >
                        Todas as regiões
                      </RegionOption>
                      {availableRegions
                        .filter(r => r.nome?.toLowerCase().includes(regionSearch.toLowerCase()))
                        .map(region => (
                          <RegionOption
                            key={region.id}
                            $active={selectedRegionFilter === String(viewMode === 'custom' ? region.id : region.nome)}
                            onClick={() => {
                              setSelectedRegionFilter(String(viewMode === 'custom' ? region.id : region.nome));
                              setShowRegionPicker(false);
                            }}
                          >
                            {region.nome}
                          </RegionOption>
                        ))}
                    </RegionDropdownList>
                  </RegionDropdown>
                )}
              </RegionPickerWrapper>
            </MapToggleBlock>
          )}
          <CanvasMap
            region={currentRegion}
            showCities={true}
            onCityClick={handleCityClick}
            onSideSheetChange={handleSideSheetChange}
            onRegionZoomChange={handleRegionZoomChange}
            customRegionsGeoJSON={customRegionsGeoJSON}
            useCustomRegions={viewMode === 'custom'}
            triggerCityId={triggerCityOpen?.id || null}
            regionProjections={regionProjections}
            totalProjection={totalProjection}
            loadingProjections={loadingCities}
            showProjections={can('cities.view.election_results')}
            canViewElectionResults={can('cities.view.election_results')}
            nextElectionYear={nextElectionYear}
            backgroundColor="#ffffff"
          />
        </div>
        {displayMode === 'list' && (
          <ListContainer onScroll={handleListScroll}>
          <TitleSection>
            <PageTitleBlock>
              <PageTitleText>
                Cidades
              </PageTitleText>
              <PageSubtitle>Visualize e gerencie todas as cidades do estado</PageSubtitle>
            </PageTitleBlock>
          </TitleSection>

          <CityStats>
            <CityStatCard $active={!filterMetReached && !filterInProgress && !filterNoResponsible} $accentColor="#3b82f6" onClick={() => { setFilterMetReached(false); setFilterInProgress(false); setFilterNoResponsible(false); }}>
              <StatCardTop><StatCardLabel>TOTAL DE CIDADES</StatCardLabel></StatCardTop>
              <StatCardValue>{loadingCities ? '–' : cityStats.total}</StatCardValue>
              <StatCardSub>cobertura estadual</StatCardSub>
            </CityStatCard>
            <CityStatCard $active={filterMetReached} $accentColor="#10b981" onClick={() => { setFilterMetReached(p => !p); setFilterInProgress(false); setFilterNoResponsible(false); }}>
              <StatCardTop><StatDot $color="#10b981"/><StatCardLabel>META 2026 ATINGIDA</StatCardLabel></StatCardTop>
              <StatCardValue>{loadingCities ? '–' : cityStats.metReached}</StatCardValue>
              <StatCardSub>/ {cityStats.total} cidades</StatCardSub>
            </CityStatCard>
            <CityStatCard $active={filterInProgress} $accentColor="#f59e0b" onClick={() => { setFilterInProgress(p => !p); setFilterMetReached(false); setFilterNoResponsible(false); }}>
              <StatCardTop><StatDot $color="#f59e0b"/><StatCardLabel>EM PROGRESSO</StatCardLabel></StatCardTop>
              <StatCardValue>{loadingCities ? '–' : cityStats.inProgress}</StatCardValue>
              <StatCardSub>0–99% da meta</StatCardSub>
            </CityStatCard>
            <CityStatCard $active={filterNoResponsible} $accentColor="#94a3b8" onClick={() => { setFilterNoResponsible(p => !p); setFilterMetReached(false); setFilterInProgress(false); }}>
              <StatCardTop><StatDot $color="#94a3b8"/><StatCardLabel>SEM RESPONSÁVEL</StatCardLabel></StatCardTop>
              <StatCardValue>{loadingCities ? '–' : cityStats.noResponsible}</StatCardValue>
              <StatCardSub>precisa atribuir</StatCardSub>
            </CityStatCard>
          </CityStats>

          {/* Mobile search + filter button */}
          <CityMobileSearchRow>
            <CityMobileSearchWrap>
              <CityMobileSearchIcon>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>
              </CityMobileSearchIcon>
              <CityMobileSearchInput
                type="text"
                placeholder="Buscar cidade..."
                value={citySearch}
                onChange={e => setCitySearch(e.target.value)}
              />
              {citySearch && (
                <CityMobileSearchClear onClick={() => setCitySearch('')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </CityMobileSearchClear>
              )}
            </CityMobileSearchWrap>
            <CityMobileFilterBtn
              $active={!!selectedRegionFilter || responsibleFilter !== 'all' || performanceFilter !== 'all' || filterMetReached || filterInProgress || filterNoResponsible}
              onClick={() => setShowMobileFilterSheet(true)}
            >
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="18" height="18"><path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round"/></svg>
              {(!!selectedRegionFilter || responsibleFilter !== 'all' || performanceFilter !== 'all' || filterMetReached || filterInProgress || filterNoResponsible) && <CityMobileFilterBadge />}
            </CityMobileFilterBtn>
          </CityMobileSearchRow>

          {showMobileFilterSheet && (
            <>
              <CityFilterSheetOverlay onClick={() => setShowMobileFilterSheet(false)} />
              <CityFilterSheet>
                <CityFilterSheetHandle />
                <CityFilterSheetHeader>
                  <CityFilterSheetTitle>Filtros</CityFilterSheetTitle>
                  <CityFilterSheetClearAll onClick={() => {
                    setSelectedRegionFilter('');
                    setResponsibleFilter('all');
                    setPerformanceFilter('all');
                    setFilterMetReached(false);
                    setFilterInProgress(false);
                    setFilterNoResponsible(false);
                  }}>Limpar tudo</CityFilterSheetClearAll>
                </CityFilterSheetHeader>
                <CityFilterSheetBody>
                  <CityFilterSheetSection>
                    <CityFilterSheetSectionLabel>Seleção rápida</CityFilterSheetSectionLabel>
                    <CityFilterSheetOption $active={filterMetReached} onClick={() => { setFilterMetReached(p => !p); setFilterInProgress(false); setFilterNoResponsible(false); }}>
                      Meta 2026 atingida
                      {filterMetReached && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </CityFilterSheetOption>
                    <CityFilterSheetOption $active={filterInProgress} onClick={() => { setFilterInProgress(p => !p); setFilterMetReached(false); setFilterNoResponsible(false); }}>
                      Em progresso (0–99%)
                      {filterInProgress && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </CityFilterSheetOption>
                    <CityFilterSheetOption $active={filterNoResponsible} onClick={() => { setFilterNoResponsible(p => !p); setFilterMetReached(false); setFilterInProgress(false); }}>
                      Sem responsável
                      {filterNoResponsible && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </CityFilterSheetOption>
                  </CityFilterSheetSection>

                  <CityFilterSheetSection>
                    <CityFilterSheetSectionLabel>Região</CityFilterSheetSectionLabel>
                    <input type="text" placeholder="Buscar região..." value={mobileRegionSearch} onChange={e => setMobileRegionSearch(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }} />
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                      <CityFilterSheetOption $active={!selectedRegionFilter} onClick={() => setSelectedRegionFilter('')}>
                        Todas as regiões
                        {!selectedRegionFilter && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </CityFilterSheetOption>
                      {availableRegions.filter(r => r.nome?.toLowerCase().includes(mobileRegionSearch.toLowerCase())).map(region => (
                        <CityFilterSheetOption key={region.id} $active={selectedRegionFilter === String(viewMode === 'custom' ? region.id : region.nome)} onClick={() => setSelectedRegionFilter(String(viewMode === 'custom' ? region.id : region.nome))}>
                          {region.nome}
                          {selectedRegionFilter === String(viewMode === 'custom' ? region.id : region.nome) && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </CityFilterSheetOption>
                      ))}
                    </div>
                  </CityFilterSheetSection>

                  <CityFilterSheetSection>
                    <CityFilterSheetSectionLabel>Responsável</CityFilterSheetSectionLabel>
                    <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                      {([{ id: 'all', nome: 'Todos os Responsáveis' }, { id: 'unassigned', nome: 'Não Atribuído' }, ...teamMembers.map(m => ({ id: String(m.id), nome: m.nome }))] as { id: string; nome: string }[]).map(m => (
                        <CityFilterSheetOption key={m.id} $active={responsibleFilter === m.id} onClick={() => setResponsibleFilter(m.id)}>
                          {m.nome}
                          {responsibleFilter === m.id && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </CityFilterSheetOption>
                      ))}
                    </div>
                  </CityFilterSheetSection>

                  <CityFilterSheetSection>
                    <CityFilterSheetSectionLabel>Performance</CityFilterSheetSectionLabel>
                    {([{ v: 'all', l: 'Todas' }, { v: 'atingida', l: 'Meta atingida (≥100%)' }, { v: 'em_progresso', l: 'Em progresso (0–99%)' }, { v: 'no_data', l: 'Sem dados' }] as { v: string; l: string }[]).map(({ v, l }) => (
                      <CityFilterSheetOption key={v} $active={performanceFilter === v} onClick={() => setPerformanceFilter(v)}>
                        {l}
                        {performanceFilter === v && <svg fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </CityFilterSheetOption>
                    ))}
                  </CityFilterSheetSection>
                </CityFilterSheetBody>
                <CityFilterSheetApply onClick={() => setShowMobileFilterSheet(false)}>
                  Ver {filteredAndSortedCities.length} cidade{filteredAndSortedCities.length !== 1 ? 's' : ''}
                </CityFilterSheetApply>
              </CityFilterSheet>
            </>
          )}

          <Controls>
            <SearchWrapper>
              <SearchIconWrap>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </SearchIconWrap>
              <SearchInput type="text" placeholder="Buscar cidade..." value={citySearch} onChange={(e) => setCitySearch(e.target.value)} />
              {citySearch && (
                <ClearSearchButton type="button" onClick={() => setCitySearch('')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </ClearSearchButton>
              )}
            </SearchWrapper>
            <FilterGroup>
              <RegionPickerWrapper ref={regionPickerRef}>
                <FilterChip type="button" $active={!!selectedRegionFilter} onClick={() => { setShowRegionPicker(p => !p); setRegionSearch(''); setShowResponsibleFilter(false); setShowPerformanceFilter(false); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  {selectedRegionFilter ? (availableRegions.find(r => String(viewMode === 'custom' ? r.id : r.nome) === selectedRegionFilter)?.nome || 'Região') : 'Região'}
                  {selectedRegionFilter ? (
                    <ChipX onClick={(e) => { e.stopPropagation(); setSelectedRegionFilter(''); }}>×</ChipX>
                  ) : (
                    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                  )}
                </FilterChip>
                {showRegionPicker && (
                  <RegionDropdown onClick={e => e.stopPropagation()}>
                    <RegionDropdownTabs>
                      <RegionDropdownTab $active={viewMode === 'traditional'} onClick={e => { e.stopPropagation(); setViewMode('traditional'); setSelectedRegionFilter(''); }}>Regiões Tradicionais</RegionDropdownTab>
                      <RegionDropdownTab $active={viewMode === 'custom'} onClick={e => { e.stopPropagation(); setViewMode('custom'); setSelectedRegionFilter(''); }}>Minhas Regiões</RegionDropdownTab>
                    </RegionDropdownTabs>
                    <RegionDropdownSearch>
                      <input autoFocus type="text" placeholder="Buscar região..." value={regionSearch} onChange={e => setRegionSearch(e.target.value)} onClick={e => e.stopPropagation()} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </RegionDropdownSearch>
                    <RegionDropdownList>
                      <RegionOption $active={!selectedRegionFilter} onClick={() => { setSelectedRegionFilter(''); setShowRegionPicker(false); }}>Todas as regiões</RegionOption>
                      {availableRegions.filter(r => r.nome?.toLowerCase().includes(regionSearch.toLowerCase())).map(region => (
                        <RegionOption key={region.id} $active={selectedRegionFilter === String(viewMode === 'custom' ? region.id : region.nome)} onClick={() => { setSelectedRegionFilter(String(viewMode === 'custom' ? region.id : region.nome)); setShowRegionPicker(false); }}>{region.nome}</RegionOption>
                      ))}
                    </RegionDropdownList>
                  </RegionDropdown>
                )}
              </RegionPickerWrapper>

              <div style={{ position: 'relative' }} data-city-filter>
                <FilterChip type="button" $active={responsibleFilter !== 'all'} onClick={() => { setShowResponsibleFilter(p => !p); setShowPerformanceFilter(false); setShowRegionPicker(false); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  {responsibleFilter === 'all' ? 'Responsável' : responsibleFilter === 'unassigned' ? 'Não Atribuído' : teamMembers.find(m => m.id.toString() === responsibleFilter)?.nome || 'Responsável'}
                  {responsibleFilter !== 'all' ? (
                    <ChipX onClick={(e) => { e.stopPropagation(); setResponsibleFilter('all'); }}>×</ChipX>
                  ) : (
                    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                  )}
                </FilterChip>
                {showResponsibleFilter && (
                  <CityFilterDropdown onClick={e => e.stopPropagation()}>
                    <CityFilterOption $active={responsibleFilter === 'all'} onClick={() => { setResponsibleFilter('all'); setShowResponsibleFilter(false); }}>Todos os Responsáveis</CityFilterOption>
                    <CityFilterOption $active={responsibleFilter === 'unassigned'} onClick={() => { setResponsibleFilter('unassigned'); setShowResponsibleFilter(false); }}>Não Atribuído</CityFilterOption>
                    {teamMembers.filter(m => m.nome.toLowerCase().includes(responsibleFilterSearch.toLowerCase())).map(m => (
                      <CityFilterOption key={m.id} $active={responsibleFilter === m.id.toString()} onClick={() => { setResponsibleFilter(m.id.toString()); setShowResponsibleFilter(false); }}>{m.nome}</CityFilterOption>
                    ))}
                  </CityFilterDropdown>
                )}
              </div>

              <div style={{ position: 'relative' }} data-city-filter>
                <FilterChip type="button" $active={performanceFilter !== 'all'} onClick={() => { setShowPerformanceFilter(p => !p); setShowResponsibleFilter(false); setShowRegionPicker(false); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  {performanceFilter === 'all' ? 'Performance' : performanceFilter === 'atingida' ? 'Meta atingida' : performanceFilter === 'em_progresso' ? 'Em progresso' : 'Sem dados'}
                  {performanceFilter !== 'all' ? (
                    <ChipX onClick={(e) => { e.stopPropagation(); setPerformanceFilter('all'); }}>×</ChipX>
                  ) : (
                    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                  )}
                </FilterChip>
                {showPerformanceFilter && (
                  <CityFilterDropdown onClick={e => e.stopPropagation()}>
                    <CityFilterOption $active={performanceFilter === 'all'} onClick={() => { setPerformanceFilter('all'); setShowPerformanceFilter(false); }}>Todas</CityFilterOption>
                    <CityFilterOption $active={performanceFilter === 'atingida'} onClick={() => { setPerformanceFilter('atingida'); setShowPerformanceFilter(false); }}>Meta atingida (≥100%)</CityFilterOption>
                    <CityFilterOption $active={performanceFilter === 'em_progresso'} onClick={() => { setPerformanceFilter('em_progresso'); setShowPerformanceFilter(false); }}>Em progresso (0–99%)</CityFilterOption>
                    <CityFilterOption $active={performanceFilter === 'no_data'} onClick={() => { setPerformanceFilter('no_data'); setShowPerformanceFilter(false); }}>Sem dados</CityFilterOption>
                  </CityFilterDropdown>
                )}
              </div>
            </FilterGroup>
          </Controls>

          <>
            <CityTable>
              <CityTableHeader $withElections={can('cities.view.election_results')}>
                <CityHeaderCell $sortable onClick={() => handleSort('nome')}>
                  CIDADE <SortIcon $direction={sortColumn === 'nome' ? sortDirection : undefined}>{sortDirection === 'asc' ? '▲' : '▼'}</SortIcon>
                </CityHeaderCell>
                <CityHeaderCell $sortable onClick={() => handleSort('responsavel')}>
                  RESPONSÁVEL <SortIcon $direction={sortColumn === 'responsavel' ? sortDirection : undefined}>{sortDirection === 'asc' ? '▲' : '▼'}</SortIcon>
                </CityHeaderCell>
                <CityHeaderCell $sortable onClick={() => handleSort('num_demandas')}>
                  DEMANDAS <SortIcon $direction={sortColumn === 'num_demandas' ? sortDirection : undefined}>{sortDirection === 'asc' ? '▲' : '▼'}</SortIcon>
                </CityHeaderCell>
                <CityHeaderCell $sortable onClick={() => handleSort('num_multiplicadores')}>
                  MULTIPLIC. <SortIcon $direction={sortColumn === 'num_multiplicadores' ? sortDirection : undefined}>{sortDirection === 'asc' ? '▲' : '▼'}</SortIcon>
                </CityHeaderCell>
                <CityHeaderCell>HISTÓRICO DE VOTOS</CityHeaderCell>
                {can('cities.view.election_results') && (
                  <CityHeaderCell $sortable onClick={() => handleSort('meta_2026')}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      META 2026 <SortIcon $direction={sortColumn === 'meta_2026' ? sortDirection : undefined}>{sortDirection === 'asc' ? '▲' : '▼'}</SortIcon>
                      {totalMeta2026 > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(var(--accent-rgb), 0.08)', color: 'var(--accent)', border: '1px solid rgba(var(--accent-rgb), 0.25)', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                          ≥ {totalMeta2026.toLocaleString('pt-BR')} votos
                        </span>
                      )}
                    </span>
                  </CityHeaderCell>
                )}
              </CityTableHeader>

              {filteredAndSortedCities.length === 0 ? (
                <div style={{ padding: '60px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  Nenhuma cidade encontrada com os filtros aplicados
                </div>
              ) : visibleCities.map((city) => {
                const hasResponsible = !!city.responsavel;
                const sortedHistory2 = [...(city.electionHistory || [])].sort((a, b) => b.ano - a.ano);
                const latestEntry = sortedHistory2[0];
                const prevEntry2 = sortedHistory2[1];
                const meta2026Meta = latestEntry?.meta_votos ?? null;
                const prevVotos = prevEntry2?.votos_obtidos ?? null;
                const meta2026ChgPct = meta2026Meta != null && prevVotos != null && prevVotos > 0
                  ? Math.round(((meta2026Meta - prevVotos) / prevVotos) * 100) : null;
                const meta2026Color: 'green' | 'yellow' | 'red' | 'neutral' = meta2026ChgPct == null ? 'neutral'
                  : meta2026ChgPct >= 0 ? 'green' : meta2026ChgPct >= -30 ? 'yellow' : 'red';

                return (
                  <CityTableRow key={city.id} $hasResponsible={hasResponsible} $withElections={can('cities.view.election_results')} onClick={(e) => {
                    if ((e.target as HTMLElement).closest('[data-dropdown]')) return;
                    handleCityClick(city.nome, { id: city.id });
                  }}>
                    <CityTableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '13px 0', minWidth: 0 }}>
                        <CityNameMain>{city.nome}</CityNameMain>
                        <CitySubtitle>
                          {city.uf}{(() => {
                              if (viewMode === 'custom') {
                                const reg = customRegions.find(r => r.cidade_ids.includes(city.id));
                                return reg ? ` · Reg. ${reg.nome}` : '';
                              }
                              return city.mesorregiao ? ` · Reg. ${city.mesorregiao}` : '';
                            })()}{city.populacao ? ` · ${city.populacao.toLocaleString('pt-BR')} hab.` : ''}
                        </CitySubtitle>
                      </div>
                    </CityTableCell>

                    <CityTableCell data-dropdown>
                      <ResponsibleCell $isAdmin={isAdmin} data-dropdown onClick={(e) => handleResponsibleClick(e, city.id)}>
                        {assigningResponsible === city.id ? (
                          <>
                            <SpinnerCircle />
                            <ResponsibleName style={{ color: 'var(--accent)' }}>Atribuindo...</ResponsibleName>
                          </>
                        ) : city.responsavel ? (
                          <>
                            <ResponsibleAvatar>
                              <UserAvatar name={city.responsavel.nome} fotoUrl={city.responsavel.foto_url} size={28} />
                            </ResponsibleAvatar>
                            <ResponsibleName>{city.responsavel.nome}</ResponsibleName>
                          </>
                        ) : (
                          <>
                            <ResponsiblePlaceholder>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            </ResponsiblePlaceholder>
                            <UnassignedText>{isAdmin ? 'Clique para atribuir' : 'Não atribuído'}</UnassignedText>
                          </>
                        )}

                      </ResponsibleCell>
                    </CityTableCell>

                    <CityTableCell style={{ fontSize: 13.5, color: '#374151' }}>
                      {city.num_demandas || 0}
                    </CityTableCell>

                    <CityTableCell style={{ position: 'relative' }}>
                      <MultiplicadoresCell count={city.num_multiplicadores || 0} multiplicadores={city.multiplicadores || []} />
                    </CityTableCell>

                    <CityTableCell $allowOverflow>
                      <SparklineCell history={city.electionHistory || []} />
                    </CityTableCell>

                    {can('cities.view.election_results') && (
                      <CityTableCell>
                        {meta2026Meta != null ? (
                          <Meta2026Cell>
                            <MetaVotesRow>
                              <MetaVotesNum $color={meta2026Color}>{meta2026Meta.toLocaleString('pt-BR')}</MetaVotesNum>
                              <MetaGoalText>votos</MetaGoalText>
                              {meta2026ChgPct != null && (
                                <MetaPercentBadge $color={meta2026Color}>
                                  {meta2026ChgPct >= 0 ? '↑' : '↓'} {meta2026ChgPct > 0 ? '+' : ''}{meta2026ChgPct}%
                                </MetaPercentBadge>
                              )}
                            </MetaVotesRow>
                          </Meta2026Cell>
                        ) : (
                          <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                        )}
                      </CityTableCell>
                    )}
                  </CityTableRow>
                );
              })}
            </CityTable>
            {cityLoadingMore && (
              <div style={{ padding: '12px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ height: 48, borderRadius: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.2s infinite' }} />
                ))}
              </div>
            )}

            <MobileList>
              {visibleCities.map((city) => {
                const hasResponsible = !!city.responsavel;
                const sortedHistory = [...(city.electionHistory || [])].sort((a, b) => b.ano - a.ano);
                const latestEntry = sortedHistory[0];
                const prevEntry = sortedHistory[1];
                const meta2026Votos = latestEntry?.votos_obtidos ?? null;
                const meta2026Meta = latestEntry?.meta_votos ?? null;
                const meta2026Pct = meta2026Votos != null && meta2026Meta
                  ? Math.round((meta2026Votos / meta2026Meta) * 100) : null;
                const mobileMetaColor: 'green' | 'yellow' | 'red' | 'neutral' = meta2026Pct == null ? 'neutral'
                  : meta2026Pct >= 100 ? 'green' : meta2026Pct >= 50 ? 'yellow' : 'red';
                const changeVsLast = (meta2026Votos != null && prevEntry?.votos_obtidos != null && prevEntry.votos_obtidos > 0)
                  ? Math.round(((meta2026Votos - prevEntry.votos_obtidos) / prevEntry.votos_obtidos) * 100) : null;
                const regionLabel = (() => {
                  if (viewMode === 'custom') {
                    const reg = customRegions.find(r => r.cidade_ids.includes(city.id));
                    return reg ? `Reg. ${reg.nome}` : '';
                  }
                  return city.mesorregiao ? `Reg. ${city.mesorregiao}` : '';
                })();
                const subtitle = [city.uf, regionLabel, city.populacao ? `${city.populacao.toLocaleString('pt-BR')} hab.` : ''].filter(Boolean).join(' · ');

                return (
                  <MobileCard key={city.id} $hasResponsible={hasResponsible} onClick={() => handleCityClick(city.nome, { id: city.id })}>
                    {/* City name + menu */}
                    <MobileCardHeader>
                      <MobileCardCityName>{city.nome}</MobileCardCityName>
                      <MobileCardMenuBtn onClick={e => { e.stopPropagation(); handleCityClick(city.nome, { id: city.id }); }}>···</MobileCardMenuBtn>
                    </MobileCardHeader>

                    {/* Subtitle: UF · Region · population */}
                    <MobileCardSubtitle>{subtitle}</MobileCardSubtitle>

                    <MobileCardDivider />

                    {/* Responsible row + badges */}
                    <MobileCardResponsibleRow>
                      <UserAvatar name={city.responsavel?.nome ?? ''} fotoUrl={city.responsavel?.foto_url} size={28} />
                      {city.responsavel
                        ? <MobileCardResponsibleName>{city.responsavel.nome}</MobileCardResponsibleName>
                        : <MobileCardNoResponsible>Sem responsável</MobileCardNoResponsible>
                      }
                      <MobileCardBadge>
                        {/* suitcase icon */}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                        {city.num_demandas || 0}
                      </MobileCardBadge>
                      <MobileCardBadge>
                        {/* people icon */}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        {city.num_multiplicadores || 0}
                      </MobileCardBadge>
                    </MobileCardResponsibleRow>

                    {/* META 2026 + sparkline two-column layout */}
                    {can('cities.view.election_results') && (
                      <MobileCardBottomRow>
                        <MobileCardBottomLeft>
                          <MobileCardMetaLeft>
                            <MobileCardMetaLabel>META 26</MobileCardMetaLabel>
                            <MobileCardMetaVotesNum $color={mobileMetaColor}>
                              {meta2026Votos?.toLocaleString('pt-BR') ?? '0'}
                            </MobileCardMetaVotesNum>
                            {meta2026Meta != null && (
                              <MobileCardMetaGoal>/ {meta2026Meta.toLocaleString('pt-BR')}</MobileCardMetaGoal>
                            )}
                            {meta2026Pct != null && (
                              <MobileCardMetaPercBadge $color={mobileMetaColor}>{meta2026Pct}%</MobileCardMetaPercBadge>
                            )}
                          </MobileCardMetaLeft>
                          {meta2026Meta != null && (
                            <MetaProgressBar>
                              <MetaProgressFill $pct={Math.min(meta2026Pct ?? 0, 100)} $color={mobileMetaColor} />
                            </MetaProgressBar>
                          )}
                        </MobileCardBottomLeft>
                        <MobileCardBottomRight>
                          <SparklineCell history={city.electionHistory || []} />
                          {prevEntry != null && (
                            <MobileCardFooterRow>
                              <MobileCardLastVotes>
                                {prevEntry.votos_obtidos?.toLocaleString('pt-BR') ?? '–'}
                              </MobileCardLastVotes>
                              {changeVsLast != null && (
                                <MobileCardChangeBadge $up={changeVsLast >= 0}>
                                  {changeVsLast >= 0 ? '↑' : '↓'}{Math.abs(changeVsLast)}%
                                </MobileCardChangeBadge>
                              )}
                            </MobileCardFooterRow>
                          )}
                        </MobileCardBottomRight>
                      </MobileCardBottomRow>
                    )}
                  </MobileCard>
                );
              })}
            </MobileList>
          </>
        </ListContainer>
        )}
      </ContentArea>

      {/* City Detail Modal */}
      <CityModal
        isOpen={isCityModalOpen}
        onClose={() => {
          setIsCityModalOpen(false);
          setSelectedCityForModal(null);
          setCityModalRegistros([]);
          setCityModalDemandas([]);
          setCityModalContatos([]);
        }}
        city={selectedCityForModal as any}
        teamMembers={teamMembers}
        onSave={handleSaveCityData as any}
        onAssignResponsible={handleAssignResponsible}
        onNewDemand={handleNewDemandFromCity}
        onDemandClick={handleDemandClickFromCity}
        onAddRegistro={handleAddCityComment}
        registros={cityModalRegistros}
        demandas={cityModalDemandas}
        contatos={cityModalContatos}
        loadingRegistros={loadingCityModalData}
        loadingDemandas={loadingCityModalData}
        loadingContatos={loadingCityModalData}
        demandPanel={{
          isOpen: isDemandDetailFromCityOpen,
          demand: selectedDemandForDetail,
          onClose: handleDemandDetailFromCityClose,
          registros: demandDetailRegistros,
          loadingRegistros: loadingDemandDetailRegistros,
          newRegistro: demandDetailNewRegistro,
          onNewRegistroChange: setDemandDetailNewRegistro,
          onAddRegistro: can('demands.updates.create') ? handleDemandDetailAddRegistro : undefined,
          addingRegistro: demandDetailAddingRegistro,
          editingRegistro: demandDetailEditingRegistro,
          editingText: demandDetailEditingText,
          onEditRegistro: (id: number) => { const r = demandDetailRegistros.find((x: any) => x.id === id); if (r) { setDemandDetailEditingRegistro(id); setDemandDetailEditingText(r.descricao_atualizacao); } },
          onSaveEdit: async (id: number) => {
            if (!session?.access_token || !selectedDemandForDetail) return;
            setDemandDetailSavingRegistroId(id);
            try {
              const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${selectedDemandForDetail.id}/registros/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ descricao_atualizacao: demandDetailEditingText }) });
              if (res.ok) { setDemandDetailRegistros(prev => prev.map((x: any) => x.id === id ? { ...x, descricao_atualizacao: demandDetailEditingText } : x)); setDemandDetailEditingRegistro(null); setDemandDetailEditingText(''); }
            } finally { setDemandDetailSavingRegistroId(null); }
          },
          onCancelEdit: () => { setDemandDetailEditingRegistro(null); setDemandDetailEditingText(''); },
          onEditTextChange: setDemandDetailEditingText,
          onDeleteRegistro: async (id: number) => {
            if (!session?.access_token || !selectedDemandForDetail) return;
            setDemandDetailDeletingRegistroId(id);
            try {
              const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${selectedDemandForDetail.id}/registros/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}` } });
              if (res.ok) setDemandDetailRegistros(prev => prev.filter((x: any) => x.id !== id));
            } finally { setDemandDetailDeletingRegistroId(null); }
          },
          savingRegistroId: demandDetailSavingRegistroId,
          deletingRegistroId: demandDetailDeletingRegistroId,
          openMenuId: demandDetailOpenMenuId,
          onMenuToggle: setDemandDetailOpenMenuId,
          canViewUpdates: can('demands.updates.view'),
          canCreateUpdates: can('demands.updates.create'),
          canEditUpdate: (r: any) => can('demands.updates.edit.all') || (can('demands.updates.edit.own') && r.usuario_id === userData?.id),
          canDeleteUpdate: (r: any) => can('demands.updates.delete.all') || (can('demands.updates.delete.own') && r.usuario_id === userData?.id),
          canEditDemand: can('demands.edit.all'),
          canDeleteDemand: can('demands.delete.all'),
          canAssignResponsible: can('demands.assign.responsible'),
          canUnassignResponsible: can('demands.unassign'),
          historico: demandDetailHistorico,
          loadingHistorico: loadingDemandDetailHistorico,
          onDemandUpdated: handleDemandUpdatedFromCity,
        }}
      />

      {/* New Demand Modal — launched from city detail, stacked above CityModal */}
      <NewDemandModal
        isOpen={isNewDemandFromCityOpen}
        onClose={() => { setIsNewDemandFromCityOpen(false); setNewDemandCityId(null); }}
        onSubmit={handleDemandFromCitySubmit}
        initialCityId={newDemandCityId}
        zIndexOverride={4500}
      />

      {/* Responsible dropdown portal — rendered outside the city list for instant open */}
      {isAdmin && openResponsibleDropdown !== null && dropdownPosition && ReactDOM.createPortal(
        (() => {
          const filteredMembers = teamMembers.filter(m => m.nome.toLowerCase().includes(responsibleSearch.toLowerCase()));
          const cityId = openResponsibleDropdown;
          return (
            <ResponsibleDropdown onMouseDown={(e) => e.stopPropagation()} style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
              {!loadingTeamMembers && (
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Buscar..."
                    value={responsibleSearch}
                    onChange={e => setResponsibleSearch(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              )}
              <DropdownOptionsList>
                {loadingTeamMembers ? (
                  [1, 2, 3, 4].map(i => (
                    <DropdownShimmerOption key={i}>
                      <DropdownShimmerAvatar />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                        <DropdownShimmerLine $width={i % 2 === 0 ? '100px' : '130px'} />
                        <DropdownShimmerLine $width="70px" />
                      </div>
                    </DropdownShimmerOption>
                  ))
                ) : filteredMembers.map((member) => (
                  <ResponsibleOption key={member.id} onClick={() => handleAssignResponsible(cityId, member.id)}>
                    <OptionAvatar><UserAvatar name={member.nome} fotoUrl={member.foto_url} size={36} /></OptionAvatar>
                    <OptionInfo>
                      <OptionName>{member.nome}</OptionName>
                      <OptionRole>{member.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor · ${member.nivel_acesso}`}</OptionRole>
                    </OptionInfo>
                  </ResponsibleOption>
                ))}
              </DropdownOptionsList>
            </ResponsibleDropdown>
          );
        })()
      , document.body)}

    </PageContainer>
  );
};

const MapTitleBlock = styled.div`
  position: absolute;
  top: 32px;
  left: 40px;
  z-index: 110;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MapTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MapTitleIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(var(--accent-rgb), 0.08);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MapTitleText = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.1;
`;

const MapSubtitleText = styled.p`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin: 4px 0 0 0;
`;

const MapTitleDividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: #e2e8f0;
  margin: 18px 0 12px;
`;

const MapToggleBlock = styled.div`
  position: absolute;
  top: 32px;
  right: 40px;
  z-index: 20;
`;

// ── New list-view design system ──────────────────────────────────────────────

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 180px;
  max-width: 480px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  svg { width: 15px; height: 15px; }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) { display: none; }
`;

const CityMobileSearchRow = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const CityMobileSearchWrap = styled.div`
  position: relative;
  flex: 1;
`;

const CityMobileSearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: flex;
  pointer-events: none;
`;

const CityMobileSearchInput = styled.input`
  width: 100%;
  padding: 10px 36px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  box-sizing: border-box;
  &::placeholder { color: #94a3b8; font-weight: 400; }
  &:focus { outline: none; border-color: #93c5fd; }
`;

const CityMobileSearchClear = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #94a3b8;
  &:hover { color: #64748b; }
  svg { width: 14px; height: 14px; }
`;

const CityMobileFilterBtn = styled.button<{ $active?: boolean }>`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1.5px solid ${p => p.$active ? 'var(--accent)' : '#e2e8f0'};
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
`;

const CityMobileFilterBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  border: 1.5px solid white;
`;

const CityFilterSheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1100;
`;

const CityFilterSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 85vh;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 1101;
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom, 16px);
`;

const CityFilterSheetHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 12px auto 0;
  flex-shrink: 0;
`;

const CityFilterSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const CityFilterSheetTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CityFilterSheetClearAll = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
`;

const CityFilterSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
`;

const CityFilterSheetSection = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const CityFilterSheetSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
  margin-bottom: 10px;
`;

const CityFilterSheetOption = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.$active ? 'var(--accent)' : '#374151'};
  cursor: pointer;
`;

const CityFilterSheetApply = styled.button`
  margin: 12px 20px;
  padding: 14px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.4)' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  svg { width: 13px; height: 13px; }
  &:hover { border-color: rgba(var(--accent-rgb), 0.4); color: var(--accent); background: rgba(var(--accent-rgb), 0.06); }
`;

const ChipX = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.2);
  color: var(--accent);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(var(--accent-rgb), 0.35); }
`;

const CityFilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 200px;
  z-index: 200;
  overflow: hidden;
`;

const CityFilterOption = styled.div<{ $active?: boolean }>`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$active ? 'var(--accent)' : '#1e293b'};
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  cursor: pointer;
  transition: background 0.1s;
  &:hover { background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.12)' : '#f8fafc'}; }
`;

const CityStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    overflow: visible;
  }

  @media (max-width: 640px) {
    display: flex;
    overflow-x: auto;
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    padding: 2px 0 10px;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const CityStatCard = styled.div<{ $active?: boolean; $accentColor?: string }>`
  padding: 16px 20px;
  border-right: 1px solid #f1f5f9;
  border-bottom: 3px solid ${p => p.$active && p.$accentColor ? p.$accentColor : 'transparent'};
  background: ${p => p.$active ? '#f8fafc' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;
  &:last-child { border-right: none; }
  &:hover { background: #f8fafc; }

  @media (max-width: 900px) {
    border-right: none;
    border-radius: 14px;
    border: 1.5px solid ${p => p.$active && p.$accentColor ? p.$accentColor + '55' : '#e8edf2'};
    background: ${p => p.$active && p.$accentColor
      ? `linear-gradient(to bottom, ${p.$accentColor}14 0%, #ffffff 100%)`
      : 'linear-gradient(to bottom, #eef0f5 0%, #ffffff 100%)'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  @media (max-width: 640px) {
    flex-shrink: 0;
    min-width: 120px;
    padding: 10px 14px;
    border-radius: 12px;
  }
`;

const StatCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const StatDot = styled.div<{ $color: string }>`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const StatCardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;

  @media (max-width: 640px) {
    font-size: 10px;
    letter-spacing: 0.4px;
  }
`;

const StatCardValue = styled.div`
  font-size: 30px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 2px;

  @media (max-width: 640px) {
    font-size: 22px;
  }
`;

const StatCardSub = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;

  @media (max-width: 640px) {
    font-size: 10px;
  }
`;

const ViewModeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px;
`;

const ViewModeBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: ${p => p.$active ? 'white' : 'transparent'};
  color: ${p => p.$active ? 'var(--accent)' : '#94a3b8'};
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  svg { width: 14px; height: 14px; }
  &:hover { color: ${p => p.$active ? 'var(--accent)' : '#64748b'}; }
`;

const CityTable = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: visible;
  @media (max-width: 768px) { display: none; }
`;

const CityTableHeader = styled.div<{ $withElections?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$withElections ? '2fr 220px 80px 80px 160px 230px' : '2fr 220px 80px 80px 160px'};
  padding: 10px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 14px 14px 0 0;
`;

const CityHeaderCell = styled.div<{ $sortable?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  ${p => p.$sortable && '&:hover { color: #64748b; }'}
`;

const CityTableRow = styled.div<{ $hasResponsible?: boolean; $withElections?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$withElections ? '2fr 220px 80px 80px 160px 230px' : '2fr 220px 80px 80px 160px'};
  border-bottom: 1px solid #f1f5f9;
  padding: 0 20px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: #f8fafc; }
  &:last-child { border-bottom: none; border-radius: 0 0 14px 14px; }
`;

const CityTableCell = styled.div<{ $allowOverflow?: boolean }>`
  font-size: 13.5px;
  color: #374151;
  font-weight: 500;
  display: flex;
  align-items: center;
  overflow: ${p => p.$allowOverflow ? 'visible' : 'hidden'};
  position: ${p => p.$allowOverflow ? 'relative' : 'static'};
  white-space: nowrap;
  min-width: 0;
`;

const CityNameMain = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CitySubtitle = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const SparklineWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const SparklineTooltip = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  background: #1e293b;
  color: white;
  font-size: 11.5px;
  font-weight: 500;
  padding: 7px 10px;
  border-radius: 7px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.5;
  @media (max-width: 768px) { display: none; }
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: #1e293b;
  }
`;

const Meta2026Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  padding: 13px 0;
`;

const MetaVotesRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  flex-wrap: nowrap;
`;

const MetaVotesNum = styled.span<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  font-size: 14px;
  font-weight: 700;
  color: ${{ green: '#059669', yellow: '#d97706', red: '#dc2626', neutral: '#64748b' }[({ green: 'green', yellow: 'yellow', red: 'red', neutral: 'neutral' } as const)['neutral']]};
  color: ${p => ({ green: '#059669', yellow: '#d97706', red: '#dc2626', neutral: '#64748b' })[p.$color]};
`;

const MetaGoalText = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const MetaPercentBadge = styled.span<{ $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: auto;
  background: ${p => ({ green: '#dcfce7', yellow: '#fef9c3', red: '#fee2e2', neutral: '#f1f5f9' })[p.$color]};
  color: ${p => ({ green: '#166534', yellow: '#854d0e', red: '#991b1b', neutral: '#64748b' })[p.$color]};
`;

const MetaProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;
`;

const MetaProgressFill = styled.div<{ $pct: number; $color: 'green' | 'yellow' | 'red' | 'neutral' }>`
  height: 100%;
  width: ${p => p.$pct}%;
  border-radius: 2px;
  background: ${p => ({ green: '#10b981', yellow: '#f59e0b', red: '#ef4444', neutral: '#94a3b8' })[p.$color]};
  transition: width 0.3s ease;
`;

export default MapDashboard;